import React, { useState, useEffect, useRef } from 'react';
// Libs
import AppAuth from 'AppAuth';
import AppFetch from 'fetch/AppFetch';
import Routes from 'Routes';
import ZywaveProxyFetch from 'fetch/ZywaveProxyFetch';
import AppConfig from 'appConfig';
import { getAppTitle, getModuleFromUrl, isZywaveUrl } from 'lib/AppHelpers';
import LoadingState from 'components/common/LoadingState';

// TP Components
import Favicon from 'react-favicon';
import { Helmet } from 'react-helmet-async';
import ZuiHeader from 'components/Layout/ZuiHeader';
import ZuiNavigation from 'components/Layout/ZuiNavigation';
import ZuiFooter from 'components/Layout/ZuiFooter';

// source: https://github.com/DefinitelyTyped/DefinitelyTyped/blob/d076add9f29db350a19bd94c37b197729cc02f87/types/react/index.d.ts

export default function AppLayout() {
  const zywaveShellRef = useRef();
  const [zywaveShellState, setZywaveShellState] = useState('');
  const [timerId, setTimerId] = useState();

  const userInfo = React.useMemo(() => (AppAuth.loggedIn() ? AppFetch.fetchUserInfo() : null), []);

  const module = getModuleFromUrl(window.location.href);

  // set the favicon and title based on module
  let favIconUrl = `${process.env.PUBLIC_URL}/zywave-favicon.ico`;

  const appTitle = getAppTitle({ module: module });

  // TODO: Need to look at where to get onAppError from. This is a placeholder for now.
  const onAppError = (error) => {
    alert(error.message);
  };

  // watch for zywave shell state changes
  // only one we care about is the "complete" state
  // when this happens set the shell state, clear the timer and set
  // timerId to false
  const onZywaveShellStateChanged = (event) => {
    if (event?.type === 'complete') {
      setZywaveShellState('complete');
      timerId && clearTimeout(timerId);
      setTimerId(false);
    }
  };

  // setup event listener for zywave-shell loading complete
  useEffect(() => {
    if (isZywaveUrl() && zywaveShellRef.current) {
      zywaveShellRef.current.addEventListener('complete', onZywaveShellStateChanged);
    }

    return () => {
      if (isZywaveUrl() && zywaveShellRef.current) {
        zywaveShellRef.current.removeEventListener('complete', onZywaveShellStateChanged);
      }
    };
  }, []);

  // check if user logged in. Set shell state to complete if they are
  // this essentially allows the ui to be drawn or in the case where user
  // is not logged in, they will be redirected to logout code
  useEffect(() => {
    const timerId = isZywaveUrl()
      ? setTimeout(() => {
          ZywaveProxyFetch.getZywaveUserInfo().then((response) => {
            if (response && response.status === 200) {
              setZywaveShellState('complete');
              return;
            }
            if (response && response.status === 401) {
              AppAuth.logout();
            }
          });
        }, 3000)
      : null;

    if (isZywaveUrl()) {
      setTimerId(timerId);
    }

    return () => clearTimeout(timerId);
  }, []);

  const isZywaveShellComplete = zywaveShellState === 'complete';

  if (isZywaveUrl()) {
    return (
      <>
        <zywave-api-proxy api-base-url={AppConfig.zywaveProxyUrl}></zywave-api-proxy>
        <zywave-shell
          unresolved
          appName={appTitle}
          logout-user-url="/edge/logout"
          ref={zywaveShellRef}
        >
          <zywave-shell-content unresolved>
            {isZywaveShellComplete ? <Routes /> : <LoadingState />}
          </zywave-shell-content>
        </zywave-shell>
      </>
    );
  }

  return (
    <zui-shell unresolved>
      <Helmet>
        <title>{appTitle}</title>
      </Helmet>
      <Favicon url={favIconUrl} />
      <ZuiHeader module={module} />
      {userInfo?.products?.length > 0 && (
        <ZuiNavigation onAppError={onAppError} userInfo={userInfo} module={module} />
      )}
      <zui-shell-content slot="content">
        <Routes />
      </zui-shell-content>
      <ZuiFooter />
    </zui-shell>
  );
}
